import { useCustomerSearch } from '@restaurant/api-client/react';
import type { CustomerDto, OrderDto } from '@restaurant/shared';
import { ArrowLeft, Plus, Search, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { OrderEditor, toEditorValue } from './OrderEditor';

/** Phone/counter order from the Orders board: pick (or add) the customer, then the same editor as in the chat. */
export function NewOrderDialog({ onCreated }: { onCreated?: (o: OrderDto) => void }) {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const reset = (o: boolean) => {
    setOpen(o);
    if (!o) setCustomer(null);
  };
  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button><Plus /> New order</Button>
      </DialogTrigger>
      <DialogContent
        title={customer ? `New order for ${customer.name ?? `+${customer.waId}`}` : 'New order'}
        description={customer ? 'Prices are taken from the menu. The customer is notified on WhatsApp when possible.' : 'Who is the order for?'}
        side="right"
      >
        {customer ? (
          <div className="grid gap-3">
            <Button variant="ghost" size="sm" className="justify-self-start" onClick={() => setCustomer(null)}><ArrowLeft /> Change customer</Button>
            <OrderEditor
              initial={toEditorValue(undefined, customer.defaultAddress)}
              submitLabel="Create & confirm"
              onSubmit={async (v) => {
                try {
                  const o = await api.orders.create({
                    customerId: customer.id,
                    type: v.type,
                    items: v.items.map((l) => ({ ...l, notes: l.notes ?? null })),
                    address: v.type === 'delivery' ? v.address : null,
                    notes: v.notes || null,
                    paymentMethod: v.paymentMethod,
                    confirm: true,
                  });
                  if (o.notification && !o.notification.sent) toast.warning(`Order #${o.number} created. The customer was not notified on WhatsApp`, { description: o.notification.reason });
                  else toast.success(`Order #${o.number} confirmed`);
                  onCreated?.(o);
                  reset(false);
                } catch (err) {
                  toastError(err);
                }
              }}
            />
          </div>
        ) : (
          <CustomerPicker onPick={setCustomer} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CustomerPicker({ onPick }: { onPick: (c: CustomerDto) => void }) {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const { data: results = [], isFetching } = useCustomerSearch(search.trim());
  const looksLikePhone = /^[+\d\s-]{6,}$/.test(search.trim());

  if (adding) return <NewCustomerForm initialPhone={looksLikePhone ? search.trim() : ''} onCancel={() => setAdding(false)} onCreated={onPick} />;

  return (
    <div className="grid gap-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input autoFocus className="pl-8" placeholder="Search name or phone number" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="max-h-80 overflow-y-auto rounded-md border">
        {isFetching && !results.length ? <div className="flex justify-center p-4"><Spinner /></div> : null}
        {results.map((c) => (
          <button key={c.id} onClick={() => onPick(c)} className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-accent">
            <span className="min-w-0">
              <span className="block truncate font-medium">{c.name ?? 'No name'}</span>
              <span className="text-xs text-muted-foreground">+{c.waId}</span>
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">{c.orderCount} order{c.orderCount === 1 ? '' : 's'}</span>
          </button>
        ))}
        {!isFetching && !results.length ? <p className="p-3 text-sm text-muted-foreground">No matching customers.</p> : null}
      </div>
      <Button variant="outline" onClick={() => setAdding(true)}><UserPlus /> New customer</Button>
    </div>
  );
}

function NewCustomerForm({ initialPhone, onCancel, onCreated }: { initialPhone: string; onCancel: () => void; onCreated: (c: CustomerDto) => void }) {
  const [f, setF] = useState({ phone: initialPhone, name: '', address: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      onCreated(await api.customers.create({ phone: f.phone, name: f.name.trim(), defaultAddress: f.address.trim() || null }));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Field label="Mobile number" hint="Local (077 123 4567) or international (+94 77 123 4567). Used for WhatsApp updates.">
        <Input autoFocus required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" />
      </Field>
      <Field label="Name">
        <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={100} />
      </Field>
      <Field label="Delivery address (optional)">
        <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} maxLength={500} />
      </Field>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Back</Button>
        <Button type="submit" disabled={busy || !f.phone.trim() || !f.name.trim()}>{busy ? <Spinner /> : null} Continue</Button>
      </div>
    </form>
  );
}
